import React, { useContext, useState } from 'react';
import { Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import { DateTimeWithTimeTooltip } from '@/shared/utils/helpers';
import {
    Button,
    CardProjects,
    SparkIcon,
    TagFeedback,
    TaskTypeIcon,
    TooltipTrigger,
    useReadOnly,
} from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { buildTaskNavigation } from '@/features/tasks/utils/navigation';
import {
    getTaskDraft,
    getDraftMissingStepLabels,
} from '@/features/tasks/utils/taskDrafts';
import { getTaskTypeIconType, isAgenticUsecase } from '@/features/tasks/utils/taskTypeIcons';
import { getTaskTypeShortLabelForUsecase } from '@/features/tasks/utils/types';
import { TaskStatusSummary } from './TaskStatusSummary.jsx';
import { MoveTasksModal } from '../MoveTasksModal.jsx';
import { useTaskActions, ConfirmMenuLabel } from './useTaskActions';
import {
    deploymentTagProps,
    getCountedLabels,
    getCreatedByLabel,
    getCreatedByPicture,
    getCurrentVersionLabel,
    getDeploymentBadge,
    getLatestUpdatedAt,
    getStatusBreakdown,
    getTaskTitle,
} from './taskSummary';
import { handleCardSelectionClick } from '@/features/tasks/utils/cardSelectionClick';

/**
 * Card displayed for a Task inside the drilled-in project view — i.e. a
 * `sub_group_name` bucket of one or more usecase rows. Matches Figma
 * "Card-Projects" (`3021:4329` regular, `3480:17756` draft): task-type tile
 * + dots menu on top, title with deployment tag, status + last-updated
 * rows, and an owner footer. Clicking the card navigates to the task (or
 * resumes the creation wizard for drafts).
 */
export const TaskCard = ({
    subGroup,
    onDeleteUsecase,
    onBulkDeleteUsecases,
    onModelDeployment,
    onDuplicateUsecase,
    onEditTask,
    onMoveUsecases,
    moveDestinations = [],
    selected = false,
    selectionKey,
    onToggleSelect,
    onRangeSelect,
    shouldSuppressClick,
}) => {
    const navigate = useNavigate();
    const { setCurrentUsecaseId, evaluationResultsReady } = useContext(GlobalContext);
    const { user: authUser } = useAuth();
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveLoading, setMoveLoading] = useState(false);
    const { readOnly } = useReadOnly();

    const usecases = subGroup?.usecases || [];
    const { taskMenuItems, onMenuOpenChange, handleDeleteTask } = useTaskActions({
        subGroup,
        onDeleteUsecase,
        onBulkDeleteUsecases,
        onModelDeployment,
        onDuplicateUsecase,
        onEditTask,
        onMoveTask: onMoveUsecases ? () => setShowMoveModal(true) : undefined,
    });

    if (usecases.length === 0) return null;

    const firstUsecase = usecases[0];
    const cardTitle = getTaskTitle(subGroup);
    const statusSummary = getCountedLabels(usecases, (uc) => uc?.status);
    const statusBreakdown = getStatusBreakdown(usecases);
    const lastUpdatedAt = getLatestUpdatedAt(usecases);
    const deploymentBadge = getDeploymentBadge(usecases);
    const currentVersionLabel = getCurrentVersionLabel(usecases);
    const createdByName = getCreatedByLabel(firstUsecase, authUser) || 'unknown';
    const createdByPicture = getCreatedByPicture(firstUsecase, authUser);
    const iconType = getTaskTypeIconType(firstUsecase?.task_type, firstUsecase?.is_multilabel);
    const isAgentic = isAgenticUsecase(firstUsecase);

    // Draft task — creation wizard was exited mid-flow. The card shows the
    // Draft tag + missing steps and clicking it resumes the wizard.
    const draftUsecase = usecases.find((uc) => getTaskDraft(uc)) || null;
    const draft = draftUsecase ? getTaskDraft(draftUsecase) : null;

    const goToTask = () => {
        const nav = buildTaskNavigation(firstUsecase, usecases, { evaluationResultsReady });
        if (!nav) return;
        if (nav.usecaseId) setCurrentUsecaseId(nav.usecaseId);
        navigate(nav.url, nav.state ? { state: nav.state } : undefined);
    };

    // Mirrors UsecasesBulkActionsBar.handleMove: keep the modal open (with
    // its own loading state) until the move resolves; close on success.
    const handleMoveTask = async (destName) => {
        if (!onMoveUsecases) return;
        setMoveLoading(true);
        try {
            const result = await onMoveUsecases(usecases, destName);
            if (result?.success !== false) setShowMoveModal(false);
        } finally {
            setMoveLoading(false);
        }
    };

    // Drafts get a single "Delete Draft" action; live tasks the full menu.
    const draftMenuItems = [
        {
            key: 'delete-draft',
            disabled: readOnly,
            icon: (
                <span style={{ display: 'inline-flex' }}>
                    <SparkIcon slug="trash" size="small" />
                </span>
            ),
            label: (
                <ConfirmMenuLabel
                    title={`Delete draft "${cardTitle}"?`}
                    body="All data associated with this draft will be permanently deleted. This action can’t be undone."
                    onConfirm={() => {
                        handleDeleteTask();
                    }}
                    primaryLabel="Delete Draft"
                    tertiaryLabel="Cancel"
                >
                    <span className="fs-unmask">Delete Draft</span>
                </ConfirmMenuLabel>
            ),
        },
    ];
    const menuItems = draft ? draftMenuItems : taskMenuItems;

    const moreActions =
        menuItems.length > 0 ? (
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                // Figma 3480:17800 — the menu opens rightwards from the dots
                // trigger (may overflow the card edge).
                placement="bottomLeft"
                onOpenChange={draft ? undefined : onMenuOpenChange}
            >
                <Button
                    variant="ghost"
                    size="small"
                    aria-label="Task actions"
                    className="fs-unmask"
                    icon={<SparkIcon slug="dots" size="medium" />}
                    onClick={(e) => e.stopPropagation()}
                />
            </Dropdown>
        ) : null;

    const deploymentTag = draft ? null : deploymentTagProps(deploymentBadge);
    const deploymentBreakdownText = deploymentBadge?.breakdown
        ?.map((item) => `${item.name}: ${item.versionLabel}`)
        .join(' · ');

    const statusContent = deploymentTag ? (
        deploymentBreakdownText ? (
            <TooltipTrigger text={deploymentBreakdownText} placement="top">
                <TagFeedback {...deploymentTag} className="fs-unmask" />
            </TooltipTrigger>
        ) : (
            <TagFeedback {...deploymentTag} className="fs-unmask" />
        )
    ) : undefined;

    return (
        <>
        <CardProjects
            selected={selected}
            title={cardTitle}
            icon={
                <TaskTypeIcon
                    type={iconType}
                    agentic={isAgentic}
                    // Richer per-usecase label (e.g. "Classification
                    // (Single-label)"); drafts without a type keep the
                    // default "Task type not set" tooltip.
                    tooltip={
                        firstUsecase?.task_type
                            ? getTaskTypeShortLabelForUsecase(firstUsecase)
                            : undefined
                    }
                />
            }
            bodyTag={
                draft ? (
                    <TagFeedback variant="processing" label="Draft" className="fs-unmask" />
                ) : undefined
            }
            moreActions={moreActions}
            statusContent={statusContent}
            description={
                draft ? (
                    <span className="fs-unmask">
                        {`Steps missing: ${getDraftMissingStepLabels(draft, draftUsecase?.autonomy_level).join(', ')}`}
                    </span>
                ) : undefined
            }
            ownerName={createdByName}
            ownerAvatarSrc={createdByPicture || undefined}
            footerAction={
                draft ? (
                    <Button
                        variant="secondary"
                        size="tiny"
                        className="fs-unmask"
                        endIcon={<SparkIcon slug="external-link-icons" size="small" />}
                        onClick={(e) => {
                            e.stopPropagation();
                            goToTask();
                        }}
                    >
                        Finish Task Creation
                    </Button>
                ) : undefined
            }
            onClick={(e) => {
                if (
                    e.target.closest?.('.spark-card-projects-footer-action') ||
                    e.target.closest?.('.spark-card-projects-header-actions') ||
                    e.target.closest?.('.ant-dropdown') ||
                    e.target.closest?.('.ant-dropdown-menu')
                ) return;
                if (handleCardSelectionClick(e, {
                    selectionKey,
                    shouldSuppressClick,
                    onToggleSelect,
                    onRangeSelect,
                })) return;
                goToTask();
            }}
        >
            {!draft && (
                <>
                    <div className="spark-card-projects-detail-row fs-unmask">
                        <SparkIcon slug="model" size="small" className="spark-card-projects-detail-icon" />
                        <dd>
                            <TaskStatusSummary
                                statuses={statusSummary}
                                versionLabel={currentVersionLabel}
                                breakdown={statusBreakdown}
                            />
                        </dd>
                    </div>
                    <div className="spark-card-projects-detail-row fs-unmask">
                        <SparkIcon slug="calendar" size="small" className="spark-card-projects-detail-icon" />
                        <dd style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span>Last updated:</span>
                            <DateTimeWithTimeTooltip value={lastUpdatedAt} emptyPlaceholder="" />
                        </dd>
                    </div>
                </>
            )}
        </CardProjects>
        {onMoveUsecases ? (
            <MoveTasksModal
                open={showMoveModal}
                itemCount={1}
                itemNames={[cardTitle]}
                destinations={moveDestinations}
                onClose={() => setShowMoveModal(false)}
                onMove={handleMoveTask}
                loading={moveLoading}
            />
        ) : null}
        </>
    );
};

export default TaskCard;
